import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { logoHorizontalDark } from "@/assets/brand";

const DemoLaunchContext = createContext(null);

// A single, app-level transition overlay for the "Launch Demo" flow (Hero, Sales Portal,
// Demo Centre). It is rendered ABOVE the router and shares the app's #0A0A0B background, so
// the Landing→demo-shell route swap, provisioning wait and any first re-render all happen
// seamlessly behind it — no jump, flicker, blank screen or layout shift. It never touches the
// normal account creation / login flow (only the three demo launchers call beginLaunch()).
export function DemoLaunchProvider({ children }) {
  const [launching, setLaunching] = useState(false);
  const location = useLocation();

  const beginLaunch = () => setLaunching(true);
  const endLaunch = () => setLaunching(false);
  // Called by the demo dashboard once its real content has fully painted, so the overlay lifts
  // to reveal the finished demo (never a skeleton mid-render). No-op for normal (non-demo) users.
  const signalReady = () => setLaunching(false);

  // Fallback: if the dashboard never signals ready (e.g. an error/empty state), still lift the
  // overlay a short while after we've entered the app shell so the user is never stuck behind it.
  useEffect(() => {
    if (!launching) return;
    if (location.pathname === "/dashboard") {
      const t = setTimeout(() => setLaunching(false), 4000);
      return () => clearTimeout(t);
    }
  }, [launching, location.pathname]);

  // Hard safety net.
  useEffect(() => {
    if (!launching) return;
    const t = setTimeout(() => setLaunching(false), 12000);
    return () => clearTimeout(t);
  }, [launching]);

  return (
    <DemoLaunchContext.Provider value={{ launching, beginLaunch, endLaunch, signalReady }}>
      {children}
      <AnimatePresence>
        {launching && (
          <motion.div
            key="demo-launch-overlay"
            data-testid="demo-launch-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-[120] bg-[#0A0A0B] flex flex-col items-center justify-center gap-7"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(720px_380px_at_50%_40%,rgba(212,175,55,0.10),transparent)]" />
            <motion.img
              src={logoHorizontalDark}
              alt="Admin Restaurant Intelligence"
              className="h-8 sm:h-9 relative z-10 opacity-90"
              initial={{ y: 6, opacity: 0 }}
              animate={{ y: 0, opacity: 0.9 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="relative z-10 flex items-center gap-3">
              <span className="w-5 h-5 rounded-full border-2 border-[#D4AF37]/25 border-t-[#D4AF37] animate-spin" />
              <span className="text-white/70 text-sm tracking-wide">Preparing your demo…</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DemoLaunchContext.Provider>
  );
}

export const useDemoLaunch = () => useContext(DemoLaunchContext);
